/**
 * Placeholder avatar art, drawn in code so the game is playable before real
 * sprites exist. The same function draws the creator preview and the in-game
 * textures, so what players design is what they get.
 */

export interface AvatarLook {
  characterType: string;
  species: string | null;
  appearance: Record<string, string>;
  /** Render data of worn items by slot (hat, top, shoes, accessory). */
  wear?: Record<string, Record<string, unknown>>;
}

type Wear = Record<string, Record<string, unknown>>;
const col = (w: Wear, slot: string, fallback: string) => (w[slot]?.color as string | undefined) ?? fallback;

export const AVATAR_W = 72;
export const AVATAR_H = 92;

function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v + amount * 255)));
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function ellipse(c: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, fill: string) {
  c.beginPath();
  c.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  c.fillStyle = fill;
  c.fill();
}

const OUTLINE = '#1d2b36';

function eyes(c: CanvasRenderingContext2D, cx: number, cy: number, style: string, gap = 9) {
  c.fillStyle = OUTLINE;
  c.strokeStyle = OUTLINE;
  c.lineWidth = 2.2;
  c.lineCap = 'round';
  for (const dx of [-gap, gap]) {
    if (style === 'happy') {
      c.beginPath();
      c.arc(cx + dx, cy + 2, 4, Math.PI * 1.1, Math.PI * 1.9);
      c.stroke();
    } else if (style === 'sleepy') {
      c.beginPath();
      c.moveTo(cx + dx - 4, cy + 1);
      c.lineTo(cx + dx + 4, cy + 1);
      c.stroke();
    } else {
      ellipse(c, cx + dx, cy, 3.2, 3.8, OUTLINE);
      ellipse(c, cx + dx + 1, cy - 1.4, 1.1, 1.1, '#ffffff');
    }
  }
}

function smile(c: CanvasRenderingContext2D, cx: number, cy: number) {
  c.strokeStyle = OUTLINE;
  c.lineWidth = 2;
  c.beginPath();
  c.arc(cx, cy, 4, Math.PI * 0.15, Math.PI * 0.85);
  c.stroke();
}

function drawCreature(c: CanvasRenderingContext2D, look: AvatarLook) {
  const fur = look.appearance.furColor ?? '#e07b39';
  const belly = shade(fur, 0.22);
  const dark = shade(fur, -0.18);
  const sp = look.species ?? 'fox';
  const cx = AVATAR_W / 2;

  // Tail sits behind the body.
  if (sp === 'fox') {
    c.beginPath();
    c.ellipse(cx + 22, 66, 9, 17, -0.7, 0, Math.PI * 2);
    c.fillStyle = fur;
    c.fill();
    ellipse(c, cx + 29, 56, 5, 6, '#fbf5ec');
  } else if (sp === 'otter') {
    c.beginPath();
    c.ellipse(cx + 20, 76, 6, 14, -1.1, 0, Math.PI * 2);
    c.fillStyle = dark;
    c.fill();
  } else {
    c.beginPath();
    c.ellipse(cx + 20, 72, 7, 15, -0.9, 0, Math.PI * 2);
    c.fillStyle = shade(fur, 0.1);
    c.fill();
  }

  // Feet and body.
  const w = look.wear ?? {};
  const feet = w.shoes ? col(w, 'shoes', dark) : dark;
  ellipse(c, cx - 9, 86, 8, 4.5, feet);
  ellipse(c, cx + 9, 86, 8, 4.5, feet);
  ellipse(c, cx, 66, 19, 20, fur);
  ellipse(c, cx, 70, 12, 13, belly);
  if (w.top) drawTopCreature(c, cx, w.top);

  // Ears or frills behind the head.
  if (sp === 'fox') {
    for (const s of [-1, 1]) {
      c.beginPath();
      c.moveTo(cx + s * 7, 22);
      c.lineTo(cx + s * 19, 4);
      c.lineTo(cx + s * 22, 26);
      c.closePath();
      c.fillStyle = fur;
      c.fill();
      c.beginPath();
      c.moveTo(cx + s * 11, 21);
      c.lineTo(cx + s * 18, 10);
      c.lineTo(cx + s * 19, 23);
      c.closePath();
      c.fillStyle = '#3a2a22';
      c.fill();
    }
  } else if (sp === 'otter') {
    for (const s of [-1, 1]) ellipse(c, cx + s * 17, 20, 5, 5, dark);
  } else {
    // Axolotl gills: three feathery fronds per side.
    for (const s of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        c.beginPath();
        c.ellipse(cx + s * (22 + i), 22 + i * 8, 9, 3.4, s * (0.5 - i * 0.45), 0, Math.PI * 2);
        c.fillStyle = '#e8708f';
        c.fill();
      }
    }
  }

  // Head.
  ellipse(c, cx, 32, 21, 19, fur);
  if (sp === 'fox') {
    ellipse(c, cx, 40, 12, 8, '#fbf5ec');
  } else if (sp === 'otter') {
    ellipse(c, cx, 39, 11, 8, belly);
  }
  eyes(c, cx, 30, look.appearance.eyes ?? 'round');
  ellipse(c, cx, 37, 3, 2.3, OUTLINE);
  smile(c, cx, 39);
  // Cheeks.
  ellipse(c, cx - 14, 38, 3.5, 2.2, 'rgba(240,120,140,0.45)');
  ellipse(c, cx + 14, 38, 3.5, 2.2, 'rgba(240,120,140,0.45)');
  if (w.accessory) drawAccessory(c, cx, 30, 9, 47, w.accessory);
  if (w.hat) drawHat(c, cx, 15, 20, w.hat);
}

function drawTopCreature(c: CanvasRenderingContext2D, cx: number, top: Record<string, unknown>) {
  const color = (top.color as string) ?? '#16808f';
  c.save();
  c.beginPath();
  c.ellipse(cx, 66, 19.5, 20.5, 0, 0, Math.PI * 2);
  c.clip();
  c.fillStyle = color;
  c.fillRect(cx - 22, 50, 44, 26);
  if (top.stripe) {
    c.fillStyle = top.stripe as string;
    for (let y = 54; y < 76; y += 7) c.fillRect(cx - 22, y, 44, 3);
  }
  c.restore();
}

function drawHat(c: CanvasRenderingContext2D, cx: number, top: number, halfW: number, hat: Record<string, unknown>) {
  const color = (hat.color as string) ?? '#e9a915';
  const band = (hat.band as string) ?? shade(color, -0.2);
  switch (hat.shape) {
    case 'cap':
      c.beginPath();
      c.ellipse(cx, top + 8, halfW * 0.85, 11, 0, Math.PI, Math.PI * 2);
      c.fillStyle = color;
      c.fill();
      c.fillStyle = band;
      c.fillRect(cx - halfW * 0.85, top + 5, halfW * 1.7, 4);
      ellipse(c, cx + halfW * 0.7, top + 8, 10, 3, shade(color, -0.1));
      break;
    case 'sunhat':
      ellipse(c, cx, top + 8, halfW + 12, 6, color);
      c.beginPath();
      c.ellipse(cx, top + 6, halfW * 0.7, 11, 0, Math.PI, Math.PI * 2);
      c.fillStyle = color;
      c.fill();
      c.fillStyle = band;
      c.fillRect(cx - halfW * 0.7, top + 2, halfW * 1.4, 3);
      break;
    case 'crown':
      for (let i = -2; i <= 2; i++) {
        ellipse(c, cx + i * (halfW / 2.4), top + 6 + Math.abs(i) * 2, 4.5, 4.5, i % 2 === 0 ? color : '#f6e27a');
        ellipse(c, cx + i * (halfW / 2.4), top + 6 + Math.abs(i) * 2, 1.6, 1.6, '#ffffff');
      }
      break;
    case 'pumpkin':
      for (const dx of [-8, 0, 8]) ellipse(c, cx + dx, top + 4, 9, 10, dx === 0 ? color : shade(color, -0.08));
      c.fillStyle = '#4f7a2a';
      c.fillRect(cx - 2, top - 10, 4, 7);
      ellipse(c, cx + 5, top - 8, 4, 2, '#6fa83a');
      if (hat.face) {
        // A carved, glowing jack-o'-lantern face.
        c.fillStyle = '#fff3a0';
        c.beginPath();
        c.moveTo(cx - 7, top + 3); c.lineTo(cx - 3, top + 3); c.lineTo(cx - 5, top - 1);
        c.moveTo(cx + 3, top + 3); c.lineTo(cx + 7, top + 3); c.lineTo(cx + 5, top - 1);
        c.fill();
        c.fillRect(cx - 6, top + 7, 12, 2.5);
      }
      break;
    case 'witch':
      ellipse(c, cx, top + 9, halfW + 10, 5, color);
      c.beginPath();
      c.moveTo(cx - halfW * 0.6, top + 8);
      c.lineTo(cx + halfW * 0.6, top + 8);
      c.lineTo(cx + 9, top - 26);
      c.closePath();
      c.fillStyle = color;
      c.fill();
      c.fillStyle = band;
      c.fillRect(cx - halfW * 0.55, top + 2, halfW * 1.1, 4);
      break;
    default: // beanie
      c.beginPath();
      c.ellipse(cx, top + 10, halfW * 0.9, 13, 0, Math.PI, Math.PI * 2);
      c.fillStyle = color;
      c.fill();
      c.fillStyle = band;
      c.fillRect(cx - halfW * 0.9, top + 6, halfW * 1.8, 5);
      ellipse(c, cx, top - 4, 4.5, 4.5, band);
  }
}

function drawAccessory(c: CanvasRenderingContext2D, cx: number, eyeY: number, gap: number, neckY: number, acc: Record<string, unknown>) {
  const color = (acc.color as string) ?? '#1d2b36';
  if (acc.shape === 'glasses') {
    c.strokeStyle = color;
    c.lineWidth = 1.8;
    for (const dx of [-gap, gap]) {
      c.beginPath();
      c.arc(cx + dx, eyeY, 5.5, 0, Math.PI * 2);
      c.stroke();
    }
    c.beginPath();
    c.moveTo(cx - gap + 5.5, eyeY);
    c.lineTo(cx + gap - 5.5, eyeY);
    c.stroke();
  } else {
    c.fillStyle = color;
    c.beginPath();
    c.roundRect(cx - 14, neckY - 3, 28, 7, 3);
    c.fill();
    c.beginPath();
    c.moveTo(cx + 6, neckY + 2);
    c.lineTo(cx + 14, neckY + 13);
    c.lineTo(cx + 2, neckY + 8);
    c.fill();
  }
}

function drawHumanoid(c: CanvasRenderingContext2D, look: AvatarLook) {
  const skin = look.appearance.skinTone ?? '#eac09f';
  const hair = look.appearance.hairColor ?? '#2b1d14';
  const style = look.appearance.hairStyle ?? 'short';
  const cx = AVATAR_W / 2;
  const w = look.wear ?? {};
  const shirt = col(w, 'top', '#9fb3c0');
  const pants = '#2f3e5c';

  if (style === 'long') {
    c.beginPath();
    c.roundRect(cx - 19, 20, 38, 40, 10);
    c.fillStyle = hair;
    c.fill();
  }
  // Legs, shoes, body, arms.
  c.fillStyle = pants;
  c.beginPath();
  c.roundRect(cx - 11, 66, 9, 18, 3);
  c.roundRect(cx + 2, 66, 9, 18, 3);
  c.fill();
  const shoes = col(w, 'shoes', '#3a2a22');
  ellipse(c, cx - 7, 86, 7, 4, shoes);
  ellipse(c, cx + 7, 86, 7, 4, shoes);
  c.fillStyle = shirt;
  c.beginPath();
  c.roundRect(cx - 15, 46, 30, 26, 9);
  c.fill();
  if (w.top?.stripe) {
    c.save();
    c.beginPath();
    c.roundRect(cx - 15, 46, 30, 26, 9);
    c.clip();
    c.fillStyle = w.top.stripe as string;
    for (let y = 50; y < 72; y += 7) c.fillRect(cx - 15, y, 30, 3);
    c.restore();
  }
  ellipse(c, cx - 17, 60, 5, 10, shirt);
  ellipse(c, cx + 17, 60, 5, 10, shirt);
  ellipse(c, cx - 17, 70, 4, 4, skin);
  ellipse(c, cx + 17, 70, 4, 4, skin);

  // Head.
  ellipse(c, cx, 30, 17, 18, skin);
  // Hair on top.
  c.fillStyle = hair;
  if (style === 'buzz') {
    c.beginPath();
    c.ellipse(cx, 22, 16, 9, 0, Math.PI, Math.PI * 2);
    c.fill();
  } else if (style === 'curly') {
    for (let i = -2; i <= 2; i++) ellipse(c, cx + i * 7, 15 + Math.abs(i) * 3, 7, 7, hair);
  } else {
    c.beginPath();
    c.ellipse(cx, 21, 18, 12, 0, Math.PI, Math.PI * 2);
    c.fill();
    c.beginPath();
    c.ellipse(cx - 4, 21, 14, 6, -0.25, 0, Math.PI);
    c.fill();
  }
  if (style === 'bun') ellipse(c, cx, 8, 8, 7, hair);
  if (style === 'ponytail') {
    c.beginPath();
    c.ellipse(cx + 19, 30, 5, 13, 0.3, 0, Math.PI * 2);
    c.fill();
  }
  eyes(c, cx, 31, look.appearance.eyes ?? 'round', 7);
  smile(c, cx, 37);
  ellipse(c, cx - 11, 37, 3, 2, 'rgba(240,120,140,0.35)');
  ellipse(c, cx + 11, 37, 3, 2, 'rgba(240,120,140,0.35)');
  if (w.accessory) drawAccessory(c, cx, 31, 7, 47, w.accessory);
  if (w.hat) drawHat(c, cx, 10, 17, w.hat);
}

/** Draws an avatar into a 72×92 box whose top-left is (0,0) of the current transform. */
export function drawAvatar(c: CanvasRenderingContext2D, look: AvatarLook) {
  // Ground shadow.
  ellipse(c, AVATAR_W / 2, 88, 22, 4, 'rgba(0,0,0,0.18)');
  if (look.characterType === 'humanoid') drawHumanoid(c, look);
  else drawCreature(c, look);
}

export function avatarCanvas(look: AvatarLook, scale = 1): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(AVATAR_W * scale);
  canvas.height = Math.ceil(AVATAR_H * scale);
  const c = canvas.getContext('2d')!;
  c.scale(scale, scale);
  drawAvatar(c, look);
  return canvas;
}

export const lookKey = (look: AvatarLook) =>
  `av:${look.characterType}:${look.species ?? '-'}:${Object.entries(look.appearance)
    .sort()
    .map(([k, v]) => `${k}=${v}`)
    .join(',')}:${JSON.stringify(look.wear ?? {})}`;

/** Resolves worn item keys to their render data using the item catalogue. */
export function wearFrom(equipped: Record<string, unknown>, catalog: Map<string, { render: Record<string, unknown> }>): Wear {
  const out: Wear = {};
  for (const [slot, key] of Object.entries(equipped)) {
    const item = typeof key === 'string' ? catalog.get(key) : undefined;
    if (item) out[slot] = item.render;
  }
  return out;
}
